// First-party analytics: fires events to our own backend so they aren't
// blocked by ad-blockers or lost to Meta signal decay. Uses fire-and-forget
// so page render is never blocked.
import { API } from "@/lib/api";

const SESSION_KEY = "jb_session_id";
const PV_DEDUPE_KEY = "jb_pv_seen";

function uuid() {
  // Small non-crypto uuid v4-ish (good enough for session id)
  return "xxxxxxxxxxxxxxxxxxxx".replace(/x/g, () =>
    Math.floor(Math.random() * 16).toString(16),
  ) + "-" + Date.now().toString(36);
}

export function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (_) {
    return "anon";
  }
}

function getUTMs() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || "",
      utm_campaign: p.get("utm_campaign") || "",
      utm_medium: p.get("utm_medium") || "",
    };
  } catch (_) {
    return { utm_source: "", utm_campaign: "", utm_medium: "" };
  }
}

function basePayload(productSlug) {
  return {
    session_id: getSessionId(),
    product_slug: productSlug || "",
    page: (typeof window !== "undefined" && window.location.pathname) || "",
    referrer: (typeof document !== "undefined" && document.referrer) || "",
    ...getUTMs(),
  };
}

/**
 * Fire-and-forget POST. Uses sendBeacon when available (survives navigation)
 * and falls back to fetch with keepalive.
 */
function sendEvent(path, body) {
  const url = `${API}${path}`;
  const json = JSON.stringify(body);
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([json], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch (_) { /* fall through to fetch */ }
  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: json,
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* ignore */ }
}

/**
 * Log a product-page (or landing) pageview. Deduped per session+path so a
 * SPA route bounce doesn't double-log.
 */
export function trackPageviewServer(productSlug = "") {
  try {
    const path = window.location.pathname;
    const key = `${PV_DEDUPE_KEY}:${path}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch (_) { /* ignore */ }

  const payload = basePayload(productSlug);
  // Best-effort page-load timing
  try {
    const nav = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
    if (nav && nav.loadEventEnd && nav.startTime !== undefined) {
      payload.page_load_ms = Math.round(nav.loadEventEnd - nav.startTime);
    }
  } catch (_) { /* ignore */ }

  sendEvent("/track/pageview", payload);
}

/**
 * Log an Amazon-CTA click. Called RIGHT before navigation — sendBeacon
 * guarantees delivery even as the browser leaves for Amazon.
 */
export function trackAmazonClickServer(productSlug) {
  sendEvent("/track/amazon-click", basePayload(productSlug));
}

/**
 * Generic event log — popup_shown, popup_dismissed, email_submitted, etc.
 */
export function trackEventServer(eventType, productSlug = "") {
  sendEvent("/track/event", {
    ...basePayload(productSlug),
    event_type: eventType,
  });
}
